import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  type InputSignal,
  type OutputEmitterRef,
  type Signal,
} from '@angular/core';
import { UiButtonComponent, UiStatComponent, UiStatGridComponent } from '@nexus/frontend-shared-ui';
import type { WishlistDetailActionKind } from '../../types/wishlist-detail-action-kind.type';
import type { WishlistDetailActionView } from '../../types/wishlist-detail-action-view.interface';
import type { WishlistDetailHeaderView } from '../../types/wishlist-detail-header-view.interface';
import { WishlistProgressSummaryComponent } from '../wishlist-progress-summary/wishlist-progress-summary.component';
import { WishlistStatusPillComponent } from '../wishlist-status-pill/wishlist-status-pill.component';
import { WishlistVisibilityIndicatorComponent } from '../wishlist-visibility-indicator/wishlist-visibility-indicator.component';

@Component({
  selector: 'wishlist-detail-header',
  imports: [
    UiButtonComponent,
    UiStatComponent,
    UiStatGridComponent,
    WishlistProgressSummaryComponent,
    WishlistStatusPillComponent,
    WishlistVisibilityIndicatorComponent,
  ],
  templateUrl: './wishlist-detail-header.component.html',
  styleUrl: './wishlist-detail-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WishlistDetailHeaderComponent {
  public readonly view: InputSignal<WishlistDetailHeaderView> = input.required<WishlistDetailHeaderView>();
  public readonly actionSelected: OutputEmitterRef<WishlistDetailActionKind> = output<WishlistDetailActionKind>();

  protected readonly hasDescription: Signal<boolean> = computed((): boolean => this.view().description !== null);
  protected readonly hasOccasion: Signal<boolean> = computed((): boolean => this.view().occasionLabel !== null);
  protected readonly hasCreator: Signal<boolean> = computed((): boolean => this.view().creatorLabel !== null);
  protected readonly hasManager: Signal<boolean> = computed((): boolean => this.view().managerLabel !== null);
  protected readonly hasAccountHint: Signal<boolean> = computed((): boolean => this.view().accountHint !== null);
  protected readonly hasPrivacyNotice: Signal<boolean> = computed((): boolean => this.view().privacyNotice !== null);
  protected readonly hasReservationHint: Signal<boolean> = computed(
    (): boolean => this.view().reservationHint !== null,
  );
  protected readonly hasStats: Signal<boolean> = computed((): boolean => this.view().stats.length > 0);
  protected readonly hasActions: Signal<boolean> = computed((): boolean => this.view().actions.length > 0);
  protected readonly isPublicHeader: Signal<boolean> = computed(
    (): boolean => this.view().mode === 'guest' || this.view().mode === 'no_access',
  );
  protected readonly showVisibility: Signal<boolean> = computed((): boolean => !this.isPublicHeader());
  protected readonly showProgress: Signal<boolean> = computed((): boolean => !this.isPublicHeader() && this.view().mode !== 'manager');
  protected readonly isStatusSuccess: Signal<boolean> = computed((): boolean => this.view().statusTone === 'success');
  protected readonly isStatusNeutral: Signal<boolean> = computed((): boolean => this.view().statusTone === 'neutral');
  protected readonly isStatusDanger: Signal<boolean> = computed((): boolean => this.view().statusTone === 'danger');
  protected readonly isStatusInfo: Signal<boolean> = computed((): boolean => this.view().statusTone === 'info');

  protected handleActionSelected(action: WishlistDetailActionView): void {
    this.actionSelected.emit(action.kind);
  }
}
